import { useEffect, useMemo, useRef } from 'react';
import { ScheduleTime, SellingMode } from '@src/core/buyerApiClient';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import {
  changeSelectedHour,
  hourSeatMapValidationResultsSelector,
  hourSeatMapValidationStatusSelector,
  hourSeatMapValidationTariffHashSelector,
  loadSeatMapValidationForHours,
  productSelector,
  selectedProductTariffHour,
  TariffsPerType,
  tariffsState,
} from '@src/stores/product';
import { createTariffHash, getDistinctScheduleTimes, hasSchedulingRequiringSeatSelection } from '@src/common/seatMaps';
import { getSelectedTariffsPerType, splitHourList } from '@src/domains/tariffs';
import {
  areAllTariffsHoursUnavailable,
  buildHoursList,
  decideHourAutoSelection,
  getScheduleTimesIncludingSoldOut,
  type HourListItem,
} from '@src/domains/products/scheduleHours';
import { track } from '@src/services/analytics';

export type UseScheduleTimeSelectionOptions = {
  /** Package single-booking: drive auto-selection from the intersected package hours instead of the current product. */
  hoursListOverride?: HourListItem[];
  /** List the times of fully booked schedulings as disabled hours instead of dropping them (new widget only). */
  includeSoldOutHours?: boolean;
};

export type ScheduleTimeSelection = {
  visible: boolean;
  hoursList: HourListItem[];
  morningHours: HourListItem[];
  lateHours: HourListItem[];
  hasOnlyOneShift: boolean;
  areAllTariffsHoursUnavailable: boolean;
  selectedHour: ScheduleTime | undefined;
  selectedTariffsPerType: TariffsPerType[];
  onSelectHour: (hour: ScheduleTime, error: boolean) => void;
};

const SEAT_MAP_VALIDATION_DEBOUNCE_MS = 300;

export function useScheduleTimeSelection(options?: UseScheduleTimeSelectionOptions): ScheduleTimeSelection {
  const dispatch = useAppDispatch();
  const widgetId = useWidgetId();
  const prodId = useSelectedProductId();
  const hoursListOverride = options?.hoursListOverride;
  const includeSoldOutHours = options?.includeSoldOutHours ?? false;

  const tariffsInformation = useAppSelector(tariffsState(widgetId));
  const selectedHour = useAppSelector(selectedProductTariffHour(widgetId));
  const product = useAppSelector(productSelector(prodId ?? ''));

  const { listOfTariffsPerType, selectedDate } = tariffsInformation;

  const selectedTariffsPerType = useMemo(() => getSelectedTariffsPerType(listOfTariffsPerType), [listOfTariffsPerType]);
  const selectedTariffsHash = useMemo(() => createTariffHash(listOfTariffsPerType), [listOfTariffsPerType]);

  const hasSchedulingRequiringSeat = useMemo(
    () => hasSchedulingRequiringSeatSelection(listOfTariffsPerType),
    [listOfTariffsPerType],
  );

  const validationStatus = useAppSelector(hourSeatMapValidationStatusSelector(widgetId));
  const validationResults = useAppSelector(hourSeatMapValidationResultsSelector(widgetId));
  const validationTariffHash = useAppSelector(hourSeatMapValidationTariffHashSelector(widgetId));

  const scheduleTimes = useMemo(
    () =>
      includeSoldOutHours
        ? getScheduleTimesIncludingSoldOut(selectedTariffsPerType)
        : getDistinctScheduleTimes(selectedTariffsPerType),
    [includeSoldOutHours, selectedTariffsPerType],
  );

  const validationTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!prodId || !hasSchedulingRequiringSeat || !selectedTariffsHash) {
      return undefined;
    }

    // Every quantity change produces a new hash, and each validation run fetches one seat map
    // overview per schedule time and allocates against it. Stepping a selector from 0 to 40 fired
    // one full round per click, so the requests queued up behind each other and the shopper waited
    // on work that was already stale. Settle first, then validate once — same debounce the tariff
    // repricing uses.
    clearTimeout(validationTimer.current);
    validationTimer.current = setTimeout(() => {
      dispatch(loadSeatMapValidationForHours(widgetId, prodId));
    }, SEAT_MAP_VALIDATION_DEBOUNCE_MS);

    return () => clearTimeout(validationTimer.current);
  }, [dispatch, widgetId, prodId, hasSchedulingRequiringSeat, selectedTariffsHash]);

  const productHoursList = useMemo(
    () =>
      buildHoursList({
        scheduleTimes,
        tariffsInformation,
        hasSchedulingRequiringSeat,
        validationStatus,
        validationTariffHash,
        selectedTariffsHash,
        selectedTariffsPerType,
        validationResults,
      }),
    [
      scheduleTimes,
      tariffsInformation,
      hasSchedulingRequiringSeat,
      validationStatus,
      validationTariffHash,
      selectedTariffsHash,
      selectedTariffsPerType,
      validationResults,
    ],
  );

  const hoursList = hoursListOverride ?? productHoursList;

  useEffect(() => {
    const { clearSelection, autoSelectValue } = decideHourAutoSelection(hoursList, selectedHour, {
      requireSelectionInList: Boolean(hoursListOverride),
    });

    if (clearSelection) {
      dispatch(changeSelectedHour({ id: widgetId, value: undefined }));
    }

    if (autoSelectValue) {
      dispatch(changeSelectedHour({ id: widgetId, value: autoSelectValue }));
    }
  }, [dispatch, hoursList, hoursListOverride, widgetId, selectedHour]);

  const [morningHours, lateHours] = useMemo(() => splitHourList(hoursList), [hoursList]);

  const hasOnlyOneShift = morningHours.length === 0 || lateHours.length === 0;

  const onSelectHour = (value: ScheduleTime, error: boolean) => {
    if (error || !product) {
      return;
    }

    track('productAdderTimeSelected', {
      productId: product.id,
      productName: product.name,
      date: selectedDate,
      time: value.formattedTime,
    });

    dispatch(
      changeSelectedHour({
        id: widgetId,
        value: selectedHour?.minutes === value.minutes ? undefined : value,
      }),
    );
  };

  const visible = Boolean(prodId && product && product.sellingMode === SellingMode.WithDateAndTime && hoursList.length);

  return {
    visible,
    hoursList,
    morningHours,
    lateHours,
    hasOnlyOneShift,
    areAllTariffsHoursUnavailable: areAllTariffsHoursUnavailable(hoursList, listOfTariffsPerType),
    selectedHour,
    selectedTariffsPerType,
    onSelectHour,
  };
}

export default useScheduleTimeSelection;
